(() => {
  const validTabIds = new Set(['beans', 'instant', 'subscription']);

  const tabIdFromHash = (hash) => {
    const tabId = hash.replace(/^#/, '');
    return validTabIds.has(tabId) ? tabId : 'beans';
  };

  const initializeCoffeeTabs = (section) => {
    if (!section || section.dataset.nscCoffeeTabsInitialized === 'true') return;

    const tablist = section.querySelector('[role="tablist"]');
    const tabs = Array.from(section.querySelectorAll('[role="tab"]'));
    const panels = Array.from(section.querySelectorAll('[data-nsc-coffee-panel]'));
    if (!tablist || tabs.length === 0 || panels.length === 0) return;

    section.dataset.nscCoffeeTabsInitialized = 'true';

    const selectTab = (tabId) => {
      const selectedTabId = validTabIds.has(tabId) ? tabId : 'beans';

      tabs.forEach((tab) => {
        const isSelected = tab.getAttribute('href') === `#${selectedTabId}`;
        tab.setAttribute('aria-selected', String(isSelected));
        tab.tabIndex = isSelected ? 0 : -1;
      });

      panels.forEach((panel) => {
        panel.hidden = panel.dataset.nscCoffeePanel !== selectedTabId;
      });
    };

    const activateTab = (tabId) => {
      const selectedTabId = validTabIds.has(tabId) ? tabId : 'beans';
      history.pushState(null, '', `#${selectedTabId}`);
      selectTab(selectedTabId);
    };

    tabs.forEach((tab, index) => {
      tab.addEventListener('click', (event) => {
        event.preventDefault();
        activateTab(tabIdFromHash(tab.getAttribute('href') || ''));
      });

      tab.addEventListener('keydown', (event) => {
        let nextIndex;

        if (event.key === 'ArrowLeft') nextIndex = (index - 1 + tabs.length) % tabs.length;
        if (event.key === 'ArrowRight') nextIndex = (index + 1) % tabs.length;
        if (event.key === 'Home') nextIndex = 0;
        if (event.key === 'End') nextIndex = tabs.length - 1;
        if (nextIndex === undefined) return;

        event.preventDefault();
        const nextTab = tabs[nextIndex];
        nextTab.focus();
        activateTab(tabIdFromHash(nextTab.getAttribute('href') || ''));
      });
    });

    const syncWithLocation = () => selectTab(tabIdFromHash(window.location.hash));
    window.addEventListener('hashchange', syncWithLocation);
    window.addEventListener('popstate', syncWithLocation);
    syncWithLocation();
  };

  const initializeSubscriptionSelector = (section) => {
    const selector = section?.querySelector('[data-nsc-subscription-selector]');
    if (!selector || selector.dataset.nscSubscriptionSelectorInitialized === 'true') return;

    const productData = selector.querySelector('[data-nsc-subscription-products]');
    const planInputs = Array.from(selector.querySelectorAll('[data-nsc-subscription-plan]'));
    const amountInputs = Array.from(selector.querySelectorAll('[data-nsc-subscription-amount]'));
    const cta = selector.querySelector('[data-nsc-subscription-cta]');
    const status = selector.querySelector('[data-nsc-subscription-status]');
    if (!productData || planInputs.length === 0 || amountInputs.length === 0 || !cta || !status) return;

    let planList;
    try {
      planList = JSON.parse(productData.textContent);
    } catch {
      return;
    }

    const plans = new Map(planList.map((plan) => [plan.id, plan]));
    selector.dataset.nscSubscriptionSelectorInitialized = 'true';

    const findVariant = (plan, amount) => {
      if (!plan || !amount) return null;

      const amountIndex = plan.amountOptionPosition ? plan.amountOptionPosition - 1 : null;
      return (
        plan.variants.find((variant) => {
          const matchesAmount =
            amountIndex === null ? variant.options.includes(amount) : variant.options[amountIndex] === amount;
          return variant.available && matchesAmount;
        }) || null
      );
    };

    const setUnavailable = () => {
      cta.removeAttribute('href');
      cta.setAttribute('aria-disabled', 'true');
      cta.classList.add('nsc-coffee__subscription-cta--unavailable');
      status.hidden = false;
    };

    const updateSelection = () => {
      let selectedPlanInput = planInputs.find((input) => input.checked && !input.disabled);
      if (!selectedPlanInput) {
        selectedPlanInput = planInputs.find((input) => !input.disabled);
        if (selectedPlanInput) selectedPlanInput.checked = true;
      }

      const plan = selectedPlanInput ? plans.get(selectedPlanInput.value) : null;
      amountInputs.forEach((input) => {
        input.disabled = !findVariant(plan, input.value);
      });

      let selectedAmountInput = amountInputs.find((input) => input.checked && !input.disabled);
      if (!selectedAmountInput) {
        selectedAmountInput = amountInputs.find((input) => !input.disabled);
        if (selectedAmountInput) selectedAmountInput.checked = true;
      }

      const variant = findVariant(plan, selectedAmountInput?.value);
      if (!plan || !variant) {
        setUnavailable();
        return;
      }

      const destination = new URL(plan.url, window.location.origin);
      destination.searchParams.set('variant', variant.id);
      cta.href = `${destination.pathname}${destination.search}${destination.hash}`;
      cta.removeAttribute('aria-disabled');
      cta.classList.remove('nsc-coffee__subscription-cta--unavailable');
      status.hidden = true;
    };

    planInputs.forEach((input) => {
      input.disabled = !plans.has(input.value);
      input.addEventListener('change', updateSelection);
    });
    amountInputs.forEach((input) => input.addEventListener('change', updateSelection));
    cta.addEventListener('click', (event) => {
      if (cta.getAttribute('aria-disabled') === 'true') event.preventDefault();
    });

    updateSelection();
  };

  const initializeAllCoffeeSections = (root = document) => {
    if (root.matches?.('[data-nsc-coffee-tabs]')) {
      initializeCoffeeTabs(root);
      initializeSubscriptionSelector(root);
    }
    root.querySelectorAll?.('[data-nsc-coffee-tabs]').forEach((section) => {
      initializeCoffeeTabs(section);
      initializeSubscriptionSelector(section);
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initializeAllCoffeeSections());
  } else {
    initializeAllCoffeeSections();
  }

  document.addEventListener('shopify:section:load', (event) => initializeAllCoffeeSections(event.target));
})();
